/**
 * React Query hooks for the v2 distributor integration:
 * - per-org settings (CRUD against `/api/orgs/{slug}/distributor-settings`)
 * - lookup-by-pointer (POST /distributors/lookup)
 *
 * Superuser system-credentials endpoints are exposed as a separate set
 * of hooks below — same shape, different prefix.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

// --- Shared shapes -------------------------------------------------------

export interface ConfigSchemaField {
  key: string;
  type: 'string' | 'bool' | 'int';
  label: string;
  default?: unknown;
  required?: boolean;
  warning?: string;
  /** When `type === "string"` and we want the input rendered as a password
   * (DigiKey/Mouser API keys), the backend can hint with `secret: true`. */
  secret?: boolean;
}

export interface ConfigSchema {
  fields: ConfigSchemaField[];
}

// --- Per-org settings ---------------------------------------------------

export interface DistributorSettings {
  distributor_slug: string;
  distributor_name: string;
  distributor_website: string | null;
  config_schema: ConfigSchema | null;
  enabled: boolean;
  priority: number;
  use_system_credentials: boolean;
  system_credentials_allowed: boolean;
  system_credentials_enabled: boolean;
  has_org_credentials: boolean;
  daily_request_count: number;
  daily_request_reset_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  adapter_available: boolean;
  /** Non-secret credential values, returned so the settings form can
   * pre-populate inputs and the user can see + edit what they entered.
   * Empty when nothing's stored OR every field is marked secret. */
  credentials: Record<string, unknown>;
  /** Schema field keys flagged `secret: true` — frontend renders
   * these as password inputs and treats blank-on-submit as "keep
   * existing value" rather than "clear it". */
  secret_field_keys: string[];
  /** Subset of `secret_field_keys` whose stored value is non-empty.
   * Lets the form show "saved — leave blank to keep" placeholders. */
  secret_fields_set: string[];
}

export interface DistributorTestResult {
  ok: boolean;
  detail: string;
}

export interface DistributorSettingsUpdate {
  enabled: boolean;
  priority: number;
  use_system_credentials: boolean;
  credentials?: Record<string, unknown> | null;
  clear_credentials?: boolean;
}

export function useDistributorSettings(orgSlug: string | undefined) {
  return useQuery<DistributorSettings[]>({
    queryKey: ['distributor-settings', orgSlug],
    queryFn: () =>
      api.get<DistributorSettings[]>(
        `/api/orgs/${orgSlug}/distributor-settings`,
      ),
    enabled: Boolean(orgSlug),
    staleTime: 30_000,
  });
}

export function useUpdateDistributorSettings(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DistributorSettingsUpdate & { distributor_slug: string }) => {
      const { distributor_slug, ...payload } = input;
      return api.putJson<DistributorSettings>(
        `/api/orgs/${orgSlug}/distributor-settings/${distributor_slug}`,
        payload,
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['distributor-settings', orgSlug] }),
  });
}

/**
 * Persist a drag-reorder of distributor settings as a list of slugs
 * in the new desired priority order. Each slug's row gets its
 * `priority` set to its 1-based index in the array; rows omitted
 * from the list keep their existing priority.
 *
 * Re-uses the per-row PUT endpoint behind the scenes (no dedicated
 * `/reorder` route on the server) — the mutation fan-outs in
 * parallel and invalidates the cache on settle.
 */
export function useReorderDistributorSettings(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      ordered: ReadonlyArray<DistributorSettings>,
    ): Promise<void> => {
      if (!orgSlug) throw new Error('No org selected');
      // Fan out parallel PUTs. Each row needs the same body shape the
      // existing endpoint accepts; we pass through enabled +
      // use_system_credentials unchanged and update priority only.
      // Rows whose priority already matches the new index are skipped
      // so a 3-row reorder doesn't generate 3 unnecessary writes.
      await Promise.all(
        ordered.map((row, idx) => {
          const newPriority = idx + 1;
          if (row.priority === newPriority) return null;
          return api.putJson<DistributorSettings>(
            `/api/orgs/${orgSlug}/distributor-settings/${row.distributor_slug}`,
            {
              enabled: row.enabled,
              priority: newPriority,
              use_system_credentials: row.use_system_credentials,
            },
          );
        }),
      );
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ['distributor-settings', orgSlug] }),
  });
}

/** Test the distributor's saved credentials by hitting a known-cheap
 * upstream call (DigiKey: token mint; LCSC: known-PN lookup). The
 * response is a structured ok/detail so the settings form can render
 * a clear ✓/✗ without re-implementing error mapping client-side. */
export function useTestDistributorCredentials(orgSlug: string | undefined) {
  return useMutation({
    mutationFn: (distributor_slug: string) =>
      api.postJson<DistributorTestResult>(
        `/api/orgs/${orgSlug}/distributor-settings/${distributor_slug}/test`,
        {},
      ),
  });
}

// --- System-wide settings (superuser) -----------------------------------

export interface SystemDistributorSettings {
  distributor_slug: string;
  distributor_name: string;
  distributor_website: string | null;
  config_schema: ConfigSchema | null;
  enabled: boolean;
  has_credentials: boolean;
  daily_request_count: number;
  daily_request_reset_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  adapter_available: boolean;
  blocked_org_slugs: string[];
}

export interface SystemDistributorSettingsUpdate {
  enabled: boolean;
  credentials?: Record<string, unknown> | null;
  clear_credentials?: boolean;
}

export function useSystemDistributorSettings() {
  return useQuery<SystemDistributorSettings[]>({
    queryKey: ['system-distributor-settings'],
    queryFn: () =>
      api.get<SystemDistributorSettings[]>('/api/admin/distributor-settings'),
    staleTime: 30_000,
  });
}

export function useUpdateSystemDistributorSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: SystemDistributorSettingsUpdate & { distributor_slug: string },
    ) => {
      const { distributor_slug, ...payload } = input;
      return api.putJson<SystemDistributorSettings>(
        `/api/admin/distributor-settings/${distributor_slug}`,
        payload,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['system-distributor-settings'] });
      // Org-level views surface system_credentials_enabled — refresh them.
      qc.invalidateQueries({ queryKey: ['distributor-settings'] });
    },
  });
}

/**
 * Imperative reveal-on-demand fetchers for distributor secret credential
 * fields (DigiKey client_secret, Mouser API key). Returns the dict of
 * decrypted secret values keyed by config_schema field name. Empty
 * dict when nothing is stored.
 */
export async function revealOrgDistributorCredentials(
  orgSlug: string,
  distributorSlug: string,
): Promise<Record<string, string>> {
  const res = await api.postJson<{ credentials: Record<string, string> }>(
    `/api/orgs/${orgSlug}/distributor-settings/${distributorSlug}/reveal-credentials`,
    {},
  );
  return res.credentials;
}

export async function revealSystemDistributorCredentials(
  distributorSlug: string,
): Promise<Record<string, string>> {
  const res = await api.postJson<{ credentials: Record<string, string> }>(
    `/api/admin/distributor-settings/${distributorSlug}/reveal-credentials`,
    {},
  );
  return res.credentials;
}

export function useSetOrgAllowlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      distributor_slug: string;
      target_org_slug: string;
      allowed: boolean;
    }) =>
      api.putJson<DistributorSettings>(
        `/api/admin/distributor-settings/${input.distributor_slug}/orgs/${input.target_org_slug}/allowlist`,
        { allowed: input.allowed },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['system-distributor-settings'] });
      qc.invalidateQueries({ queryKey: ['distributor-settings'] });
    },
  });
}

// --- Lookup-by-pointer --------------------------------------------------

export interface LookupResultPriceBreak {
  qty: number;
  price_unit_cents: number;
  currency: string;
}

export interface LookupResult {
  distributor_slug: string;
  distributor_pn: string;
  supplier_url: string;
  mpn: string | null;
  manufacturer: string | null;
  description: string | null;
  package: string | null;
  datasheet_url: string | null;
  image_url: string | null;
  price_breaks: LookupResultPriceBreak[];
  moq: number | null;
  order_multiple: number | null;
  lead_time_days: number | null;
  package_form: string | null;
  stock_qty: number | null;
  category_hint: string | null;
  /** Server-resolved PartCategory id when the smart resolver finds a
   * match for the lookup result's category breadcrumb / description.
   * Null when nothing resolved. The form's New Part dialog uses this
   * to pre-populate the category dropdown when the user clicks "Use
   * this data". */
  category_id: number | null;
  category_name: string | null;
  /** Parsed from the upstream's structured parameter list (e.g.
   * LCSC's `paramVOList`). Each is null when missing or unparseable.
   * The frontend pre-fills the corresponding form field on "Use this
   * data" so the user doesn't have to retype values that already
   * appear in the description. */
  value: string | null;
  tolerance: string | null;
  dielectric: string | null;
  voltage_v: number | null;
  voltage_v_max: number | null;
  current_a: number | null;
  power_w: number | null;
  vf: number | null;
  vr: number | null;
  frequency_hz: number | null;
  temp_min_c: number | null;
  temp_max_c: number | null;
  pin_count: number | null;
  polarized: boolean | null;
  mounting: string | null;
  /** Type-specific specs introduced by migration 0013. Each lives in
   * its own Part column so the canonical `value`/`voltage_v`/`current_a`
   * triple stays unambiguous. The form renders whichever are non-null
   * for the resolved category. */
  polarity: string | null;
  output_voltage_v: number | null;
  output_voltage_v_max: number | null;
  output_current_a: number | null;
  regulator_output_type: string | null;
  voltage_clamp_v: number | null;
  hold_current_a: number | null;
  trip_current_a: number | null;
  color: string | null;
  wavelength_nm: number | null;
  pitch_mm: number | null;
  /** 0014 additions — see `app/models/parts.py` for column rationale. */
  luminous_intensity_mcd: number | null;
  load_capacitance_pf: number | null;
  rds_on_ohms: number | null;
  hfe: number | null;
  lifecycle: string | null;
  rohs: boolean | null;
  parameters: Record<string, string> | null;
  part_id: number | null;
  part_number: string | null;
  part_created: boolean;
  part_supplier_id: number | null;
  distributor_name: string | null;
  fetched_at: string | null;
  /** Populated on the *preview* response only, when the server finds
   * an existing part with the same (distributor, PN, org) triple
   * already in the catalog. Lets the wizard surface "you already have
   * this as AR10042" before the user clicks "Use this data". Null on
   * the commit response (the `part_id` field serves there). */
  existing_part_id?: number | null;
  existing_part_number?: string | null;
}

export interface LookupRequest {
  url?: string;
  distributor_slug?: string;
  pn?: string;
  /** Run the adapter and return the DTO without persisting. Used by
   * the "review then commit" flow in NewPartModal. */
  preview?: boolean;
  /** User edits to apply on top of the looked-up DTO when persisting.
   * Each non-null key wins over the DTO's value for that field. Only
   * consulted when `preview` is false (or omitted). */
  part_overrides?: Record<string, unknown> | null;
}

export function useDistributorLookup(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LookupRequest) =>
      api.postJson<LookupResult>(
        `/api/orgs/${orgSlug}/distributors/lookup`,
        input,
      ),
    onSuccess: (result) => {
      // The lookup may have created a new Part — invalidate the parts
      // list and the suppliers cache for that part so freshly-created
      // rows show up without a manual refresh.
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
      if (result.part_id) {
        qc.invalidateQueries({
          queryKey: ['suppliers', orgSlug, result.part_id],
        });
        qc.invalidateQueries({
          queryKey: ['attachments', orgSlug, result.part_id],
        });
      }
    },
  });
}
